import type { PageParam, PageResult, PagedMetaEnvelope, PagedQueryArg } from './paged-types';
import { buildPagedQueryString } from './query-params';

export type SupportPackageItem = {
    id: string;
    message: string;
    url: string;
    state: string;
    userId: string;
    subscriptionId: string;
    createdAt: string;
};

type SupportPackageApiItem = {
    id?: string;
    message?: string | null;
    url?: string | null;
    state?: string | null;
    userId?: string | null;
    subscriptionId?: string | null;
    createdAt?: string | null;
};

type SupportPackagesApiResponse = {
    supportPackages?: SupportPackageApiItem[];
    meta?: PagedMetaEnvelope;
};

type SupportPackageApiItemWithId = SupportPackageApiItem & { id: string };

const hasId = (pkg: SupportPackageApiItem): pkg is SupportPackageApiItemWithId =>
    typeof pkg.id === 'string';

const transformSupportPackage = (pkg: SupportPackageApiItemWithId): SupportPackageItem => ({
    id: pkg.id,
    message: pkg.message ?? '',
    url: pkg.url ?? '',
    state: pkg.state ?? 'A',
    userId: pkg.userId ?? '',
    subscriptionId: pkg.subscriptionId ?? '',
    createdAt: pkg.createdAt ?? '',
});

export type SupportPackagesPageResult = PageResult<SupportPackageItem>;
export type SupportPackagesQueryArg = PagedQueryArg;

export const supportPackagesInfiniteQuery = {
    query: ({
        queryArg,
        pageParam,
    }: {
        queryArg: SupportPackagesQueryArg;
        pageParam: PageParam;
    }) => ({
        url: `/mp/1/supportPackages?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SupportPackagesApiResponse): SupportPackagesPageResult => {
        const rows = (response?.supportPackages ?? []).filter(hasId).map(transformSupportPackage);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 0,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: SupportPackagesPageResult,
            _allPages: SupportPackagesPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
};
